import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Film, X, Upload, Music, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Sprint 38.3 — vertical reel editor (browser-side, ffmpeg.wasm).
 * Lazy-loads @ffmpeg/ffmpeg only when the user opens the editor.
 * Optional custom song: uploaded + stored server-side, then muxed into the
 * rendered MP4 in the browser.
 */
export default function ReelEditor({ auraId, photos = [], onClose, onUploaded }) {
  const [selected, setSelected] = useState([]);
  const [songs, setSongs] = useState([]);
  const [songId, setSongId] = useState(null);     // selected song id, or null
  const [uploadingSong, setUploadingSong] = useState(false);
  const [duration, setDuration] = useState(15);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const songFileInput = useRef(null);
  // Keep the just-uploaded File objects so we can mux without re-downloading.
  const localFiles = useRef(new Map()); // songId -> File

  useEffect(() => {
    if (!auraId) return;
    axios.get(`${API_URL}/api/auras/${auraId}/songs`, { withCredentials: true })
      .then(r => setSongs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSongs([]));
  }, [auraId]);

  const toggle = (m) => {
    setSelected(s => {
      if (s.find(x => x.id === m.id)) return s.filter(x => x.id !== m.id);
      if (s.length >= 10) return s;
      return [...s, m];
    });
  };

  const handleSongFile = async (e) => {
    const file = e.target.files?.[0];
    if (songFileInput.current) songFileInput.current.value = '';
    if (!file) return;
    if (!file.type.startsWith('audio/')) { alert('Please choose an audio file'); return; }
    setUploadingSong(true);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      const { data } = await axios.post(`${API_URL}/api/auras/${auraId}/songs`, fd, { withCredentials: true });
      localFiles.current.set(data.id, file);
      setSongs(s => [data, ...s]);
      setSongId(data.id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Song upload failed');
    } finally {
      setUploadingSong(false);
    }
  };

  const deleteSong = async (id) => {
    if (!window.confirm('Delete this song?')) return;
    try {
      await axios.delete(`${API_URL}/api/songs/${id}`, { withCredentials: true });
      localFiles.current.delete(id);
      setSongs(s => s.filter(x => x.id !== id));
      if (songId === id) setSongId(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed');
    }
  };

  const render = async () => {
    if (!selected.length) return;
    setBusy(true);
    setProgress('Loading ffmpeg…');
    let blob;
    try {
      const { FFmpeg } = await import('@ffmpeg/ffmpeg');
      const { fetchFile } = await import('@ffmpeg/util');
      const ffmpeg = new FFmpeg();
      await ffmpeg.load();
      setProgress('Encoding…');
      const perClip = duration / selected.length;
      for (let i = 0; i < selected.length; i++) {
        const p = selected[i];
        const url = `${API_URL}${p.url}`;
        await ffmpeg.writeFile(`in${i}.jpg`, await fetchFile(url));
        await ffmpeg.exec([
          '-loop', '1', '-i', `in${i}.jpg`, '-t', String(perClip),
          '-vf', "scale=1080:1920:force_original_aspect_ratio=cover,zoompan=z='zoom+0.001':d=125:s=1080x1920",
          '-r', '30', '-c:v', 'libx264', '-pix_fmt', 'yuv420p', '-y', `clip${i}.mp4`,
        ]);
      }
      const concatList = selected.map((_, i) => `file 'clip${i}.mp4'`).join('\n');
      await ffmpeg.writeFile('list.txt', new TextEncoder().encode(concatList));
      await ffmpeg.exec(['-f', 'concat', '-safe', '0', '-i', 'list.txt', '-c', 'copy', '-y', 'out.mp4']);

      let outName = 'out.mp4';
      const song = songs.find(s => s.id === songId);
      if (song) {
        setProgress('Adding song…');
        const ext = (song.url.split('.').pop() || 'mp3').toLowerCase();
        const songName = `song.${ext}`;
        const localFile = localFiles.current.get(song.id);
        await ffmpeg.writeFile(songName, await fetchFile(localFile || `${API_URL}${song.url}`));
        // Mux: keep the video, add the song's audio, and pin the output to the
        // reel length. A longer song is trimmed; a shorter song just ends early
        // (the video always plays full length — `-shortest` would wrongly cut it).
        await ffmpeg.exec([
          '-i', 'out.mp4', '-i', songName,
          '-map', '0:v', '-map', '1:a',
          '-c:v', 'copy', '-c:a', 'aac', '-t', String(duration), '-y', 'final.mp4',
        ]);
        outName = 'final.mp4';
      }

      const data = await ffmpeg.readFile(outName);
      blob = new Blob([data.buffer], { type: 'video/mp4' });
    } catch (e) {
      alert('Reel render failed: ' + (e?.message || e));
      setBusy(false);
      return;
    }
    setProgress('Uploading…');
    try {
      const fd = new FormData();
      fd.append('file', blob, `reel-${Date.now()}.mp4`);
      fd.append('guest_name', 'Reel Editor');
      const params = auraId ? `?aura_id=${auraId}` : '';
      await axios.post(`${API_URL}/api/media/upload${params}`, fd, { withCredentials: true });
      setProgress('Done!');
      onUploaded?.();
      onClose?.();
    } catch (e) {
      alert('Upload failed');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-midnight border border-white/10 rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl text-champagne flex items-center gap-2"><Film className="w-6 h-6" /> Make a Reel</h2>
          <button aria-label="Close" onClick={onClose} className="text-text-muted hover:text-text-light p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center"><X /></button>
        </div>

        <div className="mb-4">
          <div className="text-sm text-text-muted mb-2">Pick up to 10 photos ({selected.length}/10)</div>
          <div className="grid grid-cols-4 gap-2 max-h-56 overflow-auto">
            {photos.filter(p => p.file_type === 'image').slice(0, 60).map(p => {
              const isSel = selected.find(x => x.id === p.id);
              return (
                <button key={p.id} onClick={() => toggle(p)}
                        className={`relative aspect-square overflow-hidden rounded border-2 ${isSel ? 'border-champagne' : 'border-transparent'}`}>
                  <img src={`${API_URL}${p.url}`} alt="" className="w-full h-full object-cover" />
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-text-muted flex items-center gap-1"><Music className="w-4 h-4" /> Song (optional)</div>
            <input ref={songFileInput} type="file" accept="audio/*" onChange={handleSongFile} className="hidden" data-testid="song-file-input" />
            <button onClick={() => songFileInput.current?.click()} disabled={uploadingSong}
                    className="text-xs px-3 py-1.5 rounded bg-white/5 text-text-light flex items-center gap-1 disabled:opacity-50">
              <Upload className="w-3 h-3" /> {uploadingSong ? 'Uploading…' : 'Upload a song'}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setSongId(null)}
                    className={`px-3 py-1 rounded text-sm ${songId === null ? 'bg-champagne text-midnight' : 'bg-white/5 text-text-light'}`}>
              No music
            </button>
            {songs.map(s => (
              <span key={s.id} className={`flex items-center rounded text-sm overflow-hidden ${songId === s.id ? 'bg-champagne text-midnight' : 'bg-white/5 text-text-light'}`}>
                <button onClick={() => setSongId(s.id)} className="px-3 py-1">{s.name}</button>
                <button onClick={() => deleteSong(s.id)} aria-label={`Delete ${s.name}`}
                        className={`px-2 py-1 ${songId === s.id ? 'hover:bg-black/10' : 'hover:bg-white/10'}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <div className="text-sm text-text-muted mb-2">Duration</div>
          <div className="flex gap-2">
            {[15, 30].map(d => (
              <button key={d} onClick={() => setDuration(d)}
                      className={`px-4 py-2 rounded ${duration === d ? 'bg-champagne text-midnight' : 'bg-white/5'}`}>
                {d}s
              </button>
            ))}
          </div>
        </div>

        {progress && <div className="text-sm text-text-muted mb-3">{progress}</div>}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-white/20 rounded">Cancel</button>
          <button onClick={render} disabled={busy || !selected.length}
                  className="px-4 py-2 bg-champagne text-midnight rounded font-medium disabled:opacity-50">
            {busy ? 'Working…' : 'Render Reel'}
          </button>
        </div>
        <p className="text-xs text-text-muted mt-3">On iOS Safari this may take 1-2 minutes per clip.</p>
      </div>
    </div>
  );
}
